import React, {Fragment, useContext, useEffect, useState} from "react";
import axiosConfig from "../../../../../Config/axios";
import {getApplicationRoute} from "../../../../../Route/RouteIndex";
import DataTable, {TableColumn} from "react-data-table-component";
import Loader from "../../../../../Layout/Loader";
import IconBtn from "../../../../../CommonElements/IconButton";
import AuthorizationContext from "../../../../../_helper/Authorization";
import type {AuthorizationContextType} from "../../../../../_helper/Authorization/AuthorizationTypes";
import WalletContext from "../../../Wallet/Context";
import {WalletContextType} from "../../../Wallet/Context/WalletContext.types";
import {Trello, Users} from "react-feather";
import {Collector, CollectorTableOptions, CollectorTableRef} from "../../Types/CollectorTypes";

interface CollectorTableProps {
    params: Record<string, unknown>;
    tableOptions?: CollectorTableOptions;
}

const CollectorTable = React.forwardRef<CollectorTableRef, CollectorTableProps>(({params, tableOptions = {delete: true, view: true}}, ref) => {
    const [loaderVisibility, setLoaderVisibility] = useState(false);
    const [collectors, setCollectors] = useState<Collector[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(10);
    const [totalRows, setTotalRows] = useState<number>(0);

    const authContext = useContext(AuthorizationContext) as AuthorizationContextType;
    const walletContext = useContext(WalletContext) as WalletContextType;
    const {errorHandler} = authContext || {};
    const {getFormattedAmount} = walletContext || {};

    React.useImperativeHandle(ref, () => ({
        getCollectors,
        addCollector,
    }));

    const getCollectors = (page: number = currentPage, limit: number = perPage) => {
        setLoaderVisibility(true);
        const config = {params: {...params, page, per_page: limit}};

        axiosConfig
            .get(getApplicationRoute('collector'), config)
            .then((response: any) => {
                if (response?.data?.data?.data) {
                    setCollectors(response.data.data.data);
                }
                if (response?.data?.data?.meta) {
                    setCurrentPage(response.data.data.meta.current_page);
                    setPerPage(response.data.data.meta.per_page);
                    setTotalRows(response.data.data.meta.total);
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    };

    useEffect(() => {
        getCollectors(1, perPage);
    }, [params]);

    const deleteCollector = (id: number) => {
        setLoaderVisibility(true);
        axiosConfig
            .delete(`${getApplicationRoute('collector')}/${id}`, {})
            .then((response: { data: { data: { id: number } } }) => {
                if (response?.data?.data) {
                    setCollectors((current) => current.filter((collectorItem) => collectorItem.id !== response.data.data.id));
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    };

    const addCollector = (collector: Collector) => {
        const collectorList = [...collectors];
        const existingCollectorIndex = collectorList.findIndex((existingCollector) => existingCollector.id === collector.id);

        if (existingCollectorIndex !== -1) {
            collectorList[existingCollectorIndex] = collector;
        } else {
            collectorList.push(collector);
        }

        setCollectors(collectorList);
    };

    const collectorTableColumns: TableColumn<Collector>[] = [
        {
            name: 'NIC',
            selector: (row: Collector) => row.nic || '',
            sortable: false,
        },
        {
            name: 'Name',
            selector: (row: Collector) => row.user?.name || row.name || '',
            sortable: false,
            right: true,
        },
        {
            name: 'Admin User',
            selector: (row: Collector) => row.created_user?.name || '',
            sortable: false,
        },
        {
            name: 'Mobile',
            selector: (row: Collector) => row.mobile || row.contact_number || '',
            sortable: false,
            right: true,
        },
        {
            name: 'Statistics',
            cell: (row: Collector) => {
                return <div className='d-flex align-items-center gap-2'>
                    <div className='d-flex align-items-center gap-1' title='Total Boards'>
                        <Trello size={16} style={{cursor: 'help'}}/>
                        <span>{row.statistics?.boards ?? 0}</span>
                    </div>
                    <div className='d-flex align-items-center gap-1' title='Total Participants'>
                        <Users size={16} style={{cursor: 'help'}}/>
                        <span>{row.statistics?.participants ?? 0}</span>
                    </div>
                </div>;
            },
            sortable: false,
        },
        {
            name: 'Wallets',
            cell: (row: Collector) => {
                const cash = row.statistics?.cash ?? row.cashWallet?.current_balance ?? 0;
                const points = row.statistics?.points ?? row.pointWallet?.current_balance ?? 0;
                return (
                    <div className='d-flex align-items-center gap-2 flex-wrap'>
                        <div className='d-flex align-items-center gap-1' title='Cash Wallet Balance'>
                            <span className={`badge badge-light-success m-r-20`}>
                                Cash Wallet
                            </span>
                            <span>{getFormattedAmount(cash, 'wallet-type-cash', 1)}</span>
                        </div>
                        <div className='d-flex align-items-center gap-1' title='Points Wallet Balance'>
                            <span className={`badge badge-light-primary m-r-20`}>
                                Points Wallet
                            </span>
                            <span>{getFormattedAmount(points, 'wallet-type-points', 1)}</span>
                        </div>
                    </div>
                );
            },
            sortable: false,
        },
        {
            name: 'Option',
            cell: (row: Collector) => (
                <div className='d-flex align-items-right gap-2'>
                    {tableOptions?.view && (
                        <IconBtn link={`${getApplicationRoute('collector')}/${row.id}`} type={'view'} permission={'read-collector'}/>
                    )}
                    {tableOptions?.delete && (
                        <IconBtn confirm={true} onclick={() => deleteCollector(row.id)} type={'delete'} permission={'delete-collector'}/>
                    )}
                </div>
            ),
            sortable: false,
        },
    ];

    return <Fragment>
        <Loader visibility={loaderVisibility}/>
        <DataTable pagination paginationServer columns={collectorTableColumns}
                   data={collectors}
                   paginationTotalRows={totalRows}
                   paginationPerPage={perPage}
                   paginationDefaultPage={currentPage}
                   onChangePage={(page) => getCollectors(page, perPage)}
                   onChangeRowsPerPage={(newPerPage, page) => {
                       setPerPage(newPerPage);
                       getCollectors(page, newPerPage);
                   }}
                   highlightOnHover={true}
                   striped={true} responsive={true}/>
    </Fragment>;
});

export default CollectorTable;

